// cleanup_hoc_to_hooks_PAYMENTS
// cleanup_convert_to_typescript_file_PAYMENTS

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import PlaceResolver, { variants } from 'components/placeResolver/PlaceResolver';
import withPlaceResolver from 'components/withPlaceResolver/withPlaceResolver';
import {
  getList,
  getPaymentTypes,
  paymentPermissions,
  wirePaymentPermissions,
  getPaymentTemplatesPermissions,
  getPaymentsPreferences,
  getFxRates
} from 'api/payments/service';
import { ACTIONS, PAYMENT_CATS } from 'constants.js';

import { getPaymentListPayload } from './web/payments.utils';

/**
 * Data normalization
 *
 * @param {object} data
 */
const expandData = (data) =>
  (data || [])
    .map((d) => ({
      ...d,
      effectiveEntryDate: d.transactionDt
    }))
    .sort((a, b) => {
      const dateA = moment(a.transactionDt, moment.ISO_8601);
      const dateB = moment(b.transactionDt, moment.ISO_8601);
      return dateB - dateA;
    });

const getAchPermissions = (data) => {
  const achPayments = (data || []).filter(
    ({ paymentCategory }) => paymentCategory === PAYMENT_CATS.ACH
  );

  // Permissions
  return {
    approve: achPayments.some(({ possibleActions }) => possibleActions.includes(ACTIONS.APPROVE)),
    reject: achPayments.some(({ possibleActions }) => possibleActions.includes(ACTIONS.REJECT))
  };
};

export const PaymentsDataProviderComponent = ({
  children,
  reload,
  results: [
    {
      data: { displayDatePicker }
    }
  ]
}) => {
  const memoizedGetList = useCallback(
    () => getList(getPaymentListPayload(displayDatePicker)),
    [displayDatePicker]
  );

  return (
    <PlaceResolver
      services={[
        memoizedGetList,
        paymentPermissions,
        getPaymentTypes,
        wirePaymentPermissions,
        getPaymentTemplatesPermissions,
        getFxRates
      ]}
      variant={variants.table}
    >
      {({
        reloads: [updateData, updatePermissions],
        results: [
          data,
          paymentPermissions,
          paymentTypes,
          permissions,
          templatePermissions,
          fxRates
        ],
        syncTimes: [lastSync]
      }) => {
        const achPermissions = getAchPermissions(data);

        const paymentApprovalEnabled =
          permissions.approve || permissions.createtax || achPermissions.approve;

        // NOTE: These permissions make the user a 'corporate' user
        const corporatePermissions = { ...permissions };
        // NOTE: createtax is used for both simplified and corporate payments
        delete corporatePermissions.createtax;
        const hasCorporatePermissions = Object.values(corporatePermissions).some(Boolean);

        return children({
          achPermissions,
          wirePermissions: {
            ...permissions
          },
          paymentPermissions,
          permissions: {
            ...permissions,
            approve: paymentApprovalEnabled
          },
          hasCorporatePermissions,
          fxRates,
          templatePermissions,
          updates: {
            updatePermissions,
            updateData
          },
          lastSync,
          data: expandData(data),
          paymentTypes,
          reload,
          displayDatePicker
        });
      }}
    </PlaceResolver>
  );
};

PaymentsDataProviderComponent.propTypes = {
  results: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
  reload: PropTypes.func.isRequired,
  children: PropTypes.func.isRequired
};

export default withPlaceResolver({
  services: [getPaymentsPreferences],
  variant: variants.table
})(PaymentsDataProviderComponent);
